import { createContext, useContext, useState } from 'react';
import service from '../service';
import { formatInboxLabel } from '../components/Inbox/utils';

const MessagesContext = createContext();

const MessagesContextProvider = ({ children }) => {
  const [unsentOutboundMessages, setUnsentOutboundMessages] = useState([]);
  const [sentOutboundMessages, setSentOutboundMessages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [smsThreadMessages, setSmsThreadMessages] = useState([]);
  const [fishList, setFishList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [mergeTags, setMergeTags] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [inboxes, setInboxes] = useState([]);
  const [userPermissions, setUserPermissions] = useState(null);
  const [showHTMLEditor, setShowHTMLEditor] = useState(false);

  const fetchSegments = async () => {
    try {
      const allSegments = await service.readAllSegments();
      setSegments(allSegments);
    } catch (error) {
      console.error('Failed to fetch segments:', error);
    }
  };

  const fetchUnsentOutboundMessages = async (recipient) => {
    const response = await service.readOutboundMessages('unsent', recipient);
    const data = await response.json();

    const sortedData = data.sort(
      (a, b) => new Date(a.sendTime) - new Date(b.sendTime),
    );

    setUnsentOutboundMessages(sortedData);
    return sortedData;
  };

  const fetchSentOutboundMessages = async (recipient) => {
    const response = await service.readOutboundMessages('sent', recipient);
    const data = await response.json();

    setSentOutboundMessages(data);
    return data;
  };

  const fetchSmsThread = async (recipient) => {
    const response = await service.readSmsThread(recipient);
    const smsThread = await response.json();
    return smsThread;
  };

  const fetchInboxSmsThreads = async (inboxId) => {
    const response = await service.readInboxSMSThreads(inboxId);
    const threads = await response.json();
    return threads;
  };

  const fetchAllFish = async (search) => {
    try {

      let query = {
        // 'page[size]': -1,
        'include':
          'sharedSubscription.subscription.subscriptionTier,subscriptions.subscriptionTier,fishProfiles,wallet',
        'include[subscriptions][filter][cancelledAt]': null,
        'include[subscriptions][filter][deletedAt]': null,
        'include[fishProfiles][filter][deletedAt]': null,
      };

      if (search) {
        query = {
          'filter[deletedAt]': null,
          'filter[0][phoneNumber]': `*${search}*`,
          'filter[1][fishProfiles][some][fName]': `*${search}*`,
          'filter[2][fishProfiles][some][lName]': `*${search}*`,
          'filter[3][fishProfiles][some][email]': `*${search}*`,
          ...query
        }
      }

      const fish = await service.readAllFish(query);
      setFishList(fish);
      return fish;
    } catch (error) {
      console.error('Failed to fetch fish:', error);
      return [];
    }
  };

  const searchAllUsers = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[deletedAt]': null,
        'filter[0][name]': `*${search}*`,
        'filter[1][phoneNumber]': `*${search}*`,
        'filter[2][email]': `*${search}*`,
      }

      const users = await service.getUsers(query);
      return users.data ?? users;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  };

  const fetchAllUsers = async () => {
    try {
      const users = await service.getUsers();
      setUserList(users.data ?? users);
      return users;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  };

  const fetchUserRoles = async () => {
    try {
      const userRoles = await service.readUserRoles();
      setUserRoles(userRoles);
      return userRoles;
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      return [];
    }
  };

  const fetchMergeTags = async (id) => {
    try {
      const response = await service.readMergeTags(id);
      const tags = await response.json();
      const tagsArray = Array.isArray(tags) ? tags : [];
      setMergeTags(tags);
      return tags;
    } catch (error) {
      console.error('Failed to fetch merge tags:', error);
      return [];
    }
  };

  const fetchAllInboxes = async () => {
    try {
      const inboxesData = await service.readAllInboxes();
      // Ensure we always set an array (API might return error object on failure)
      if (Array.isArray(inboxesData)) {
        setInboxes(inboxesData);
        return inboxesData;
      } else {
        console.error('Unexpected inboxes response:', inboxesData);
        setInboxes([]);
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch inboxes:', error);
      setInboxes([]);
      return [];
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const permissions = await service.getUserPermissions(userId);
      setUserPermissions(permissions);
      return permissions;
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      return null;
    }
  };

  const getInboxDisplay = (inboxId) => {
    const inbox = inboxes.find((inbox) => inbox.id === inboxId);
    return formatInboxLabel(inbox);
  };

  const getInboxById = (inboxId) => {
    return inboxes.find((inbox) => inbox.id === inboxId);
  };

  const fetchMessageTemplates = async () => {
    try {
      const templates = await service.getMessageTemplates();
      setMessageTemplates(templates);
      return templates;
    } catch (error) {
      console.error('Failed to fetch message templates:', error);
      return [];
    }
  };


  const isHtmlDesignBlank = (design) => {
    if (!design || !design.body || !Array.isArray(design.body.rows)) return true;

    return !design.body.rows.some(row =>
      row?.columns?.some(col =>
        Array.isArray(col?.contents) && col.contents.length > 0
      )
    );
  }

  return (
    <MessagesContext.Provider
      value={{
        unsentOutboundMessages,
        sentOutboundMessages,
        segments,
        fetchUnsentOutboundMessages,
        fetchSentOutboundMessages,
        fetchSmsThread,
        fetchInboxSmsThreads,
        smsThreadMessages,
        setSmsThreadMessages,
        fetchSegments,
        fishList,
        fetchAllFish,
        mergeTags,
        fetchMergeTags,
        messageTemplates,
        fetchMessageTemplates,
        inboxes,
        fetchAllInboxes,
        getInboxDisplay,
        getInboxById,
        userPermissions,
        fetchUserPermissions,
        fetchAllUsers,
        searchAllUsers,
        userList,
        fetchUserRoles,
        userRoles,
        showHTMLEditor,
        setShowHTMLEditor,
        isHtmlDesignBlank,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

const useMessages = () => useContext(MessagesContext);

export { useMessages };
export default MessagesContextProvider;
